import React, { useState } from 'react'
import { IssueEntity } from '../../types/IssueEntity'
import { IssueType } from '@finance-lab/utils/types'
import { Box, Grid, Stack, Typography } from '@mui/material'
import { ScatterSeriesType } from '@mui/x-charts'
import { useTranslation } from 'react-i18next'
import { formatBigMoney, formatDateYYYYMMDD, formatMoney } from '../../packages/utils/numberFormatters'
import DropdownSelect from '../../pages/components/DropdownSelect'
import {
    calculateDilution,
    calculateDiscount,
    calculatePriceChange,
    getCommitmentsInPercentage,
    getIssueOfferSize,
    getIssueSubscribedSize,
    getIssueSubscriptionQuota,
    getIssueSubscriptionQuotaExclGuarantors,
    getIssueSubscriptionQuotaExclGuarantorsAndCommitments,
} from '../../pages/issues/utils'
import { DataGridPro, GridColDef, GridColType, GridRenderCellParams, useGridApiRef } from '@mui/x-data-grid-pro'
import { getMuiDataGridLocaleText } from '../../i18n/localeUtils'
import { useTheme } from '@emotion/react'
import { filterIssueForGraphs } from './utils'
import { FLScatterChart, ScatterSerie } from './fl-scatter-chart'
import { AXIS_OPTIONS_LABELS, formatValue } from './fl-scatter-chart/utils'
import { useLogUserEvent } from '../../hooks/useLogUserEvent'
import UserEventApi from '../../api/UserEventApi'
import { exportDataGridProps } from './issue-market/utils'

const AXIS_OPTIONS_GROUPS: Record<string, (keyof typeof AXIS_OPTIONS_LABELS)[]> = {
    Allmänt: ['avgTurnover', 'dilutionActual', 'dilutionMax', 'offerSize', 'subscribedSize', 'preMoney'],
    Garantiersättning: ['feeCash', 'feeUnits'],
    Kursutveckling: ['priceChangeMax', 'priceChange3m', 'priceChange6m', 'priceChange12m'],
    Rabatter: ['vwap10d', 'vwap10dTerp'],
    Säkerställande: ['commitment', 'totalCommitment'],
    Utfall: ['subscriptionQuota', 'subscriptionQuotaExclGuarantors', 'subscriptionQuotaExclGuarantorsAndCommitments'],
}

export type AxisPropOption = keyof typeof AXIS_OPTIONS_LABELS

export type Boundaries = {
    minX: number
    maxX: number
    minY: number
    maxY: number
}

const getOptionsGroups = (issueType: IssueType) => {
    if (issueType === 'Directed issue') {
        const options = { ...AXIS_OPTIONS_GROUPS }
        delete options['Garantiersättning']
        delete options['Utfall']
        delete options['Säkerställande']

        return {
            ...options,
            Allmänt: options['Allmänt'].filter((o) => !['offerSize', 'dilutionMax'].includes(o)),
            Rabatter: options['Rabatter'].filter((o) => !['vwap10dTerp'].includes(o)),
        }
    }

    return AXIS_OPTIONS_GROUPS
}

export const ScatterPlot = ({
    isLoading,
    issueType,
    issues,
}: {
    isLoading: boolean
    issueType: Extract<IssueType, 'Rights issue' | 'Directed issue'>
    issues: IssueEntity[]
}) => {
    const { i18n } = useTranslation()
    const locale = i18n.language
    const gridRef = useGridApiRef()
    const theme = useTheme()
    const [currentPage, setCurrentPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)
    const [selectedXAxisProperty, setSelectedXAxisProperty] = useState<AxisPropOption>(
        getOptionsGroups(issueType)['Rabatter'][0],
    )
    const [selectedYAxisProperty, setSelectedYAxisProperty] = useState<AxisPropOption>(
        getOptionsGroups(issueType)['Allmänt'][issueType === 'Rights issue' ? 2 : 1],
    )
    const [selectionBoundaries, setSelectionBoundaries] = useState<Boundaries | null>(null)
    const [zoomBoundaries, setZoomBoundaries] = useState<Boundaries | null>(null)

    useLogUserEvent(UserEventApi.EVENT_TYPES.STATS_TREND_GRAPHS_SELECTION, {
        selectedXAxisProperty,
        selectedYAxisProperty,
    })

    const handleZoom = React.useCallback(
        (boundaries: Boundaries | null) => {
            setZoomBoundaries(boundaries)
        },
        [setZoomBoundaries],
    )

    const handleSelectionChange = React.useCallback(
        (
            pos: {
                minX: number
                maxX: number
                minY: number
                maxY: number
            } | null,
        ) => {
            setSelectionBoundaries(pos)
        },
        [setSelectionBoundaries],
    )

    const seriesData = issues
        .filter((issue) => filterIssueForGraphs(issue))
        .map((issue) => {
            const { fees, id, preMoney, shareName, subEndDate, tradesStats } = issue
            const { avgTurnover = null, vwap = null } =
                tradesStats.find(
                    ({ periodType, numberOfDays }) => periodType === 'prePublication' && numberOfDays === 10,
                ) || {}
            const vwap10d = calculateDiscount(issue, vwap, false)
            const vwap10dTerp = calculateDiscount(issue, vwap, true)
            const { totalCommitment = null, commitment = null } = getCommitmentsInPercentage(issue) || {}
            const { maxDilution: dilutionMax, actualDilution: dilutionActual } = calculateDilution(issue)

            return {
                avgTurnover,
                commitment,
                dilutionActual,
                dilutionMax,
                feeCash: fees?.find(({ feeLevel }) => feeLevel === 1)?.feeCash || null,
                feeUnits: fees?.find(({ feeLevel }) => feeLevel === 1)?.feeUnits || null,
                offerSize: getIssueOfferSize(issue),
                id,
                key: id,
                preMoney: preMoney === null ? null : +preMoney,
                priceChangeMax: calculatePriceChange(
                    tradesStats.find(({ periodType }) => periodType === 'postIssueMax'),
                    issue.subscriptionRate,
                ),
                priceChange3m: calculatePriceChange(
                    tradesStats.find(({ periodType }) => periodType === 'postIssue3m'),
                    issue.subscriptionRate,
                ),
                priceChange6m: calculatePriceChange(
                    tradesStats.find(({ periodType }) => periodType === 'postIssue6m'),
                    issue.subscriptionRate,
                ),
                priceChange12m: calculatePriceChange(
                    tradesStats.find(({ periodType }) => periodType === 'postIssue12m'),
                    issue.subscriptionRate,
                ),
                subEndDate,
                subscribedSize: getIssueSubscribedSize(issue),
                subscriptionQuota: getIssueSubscriptionQuota(issue),
                subscriptionQuotaExclGuarantors: getIssueSubscriptionQuotaExclGuarantors(issue),
                subscriptionQuotaExclGuarantorsAndCommitments:
                    getIssueSubscriptionQuotaExclGuarantorsAndCommitments(issue),
                totalCommitment: Math.min(totalCommitment, 100),
                label: shareName || '-',
                vwap10d,
                vwap10dTerp,
            }
        })
        .filter((item) => {
            if (item[selectedXAxisProperty] === null || item[selectedYAxisProperty] === null) return false

            if (zoomBoundaries) {
                return (
                    item[selectedXAxisProperty] >= zoomBoundaries.minX &&
                    item[selectedXAxisProperty] <= zoomBoundaries.maxX &&
                    item[selectedYAxisProperty] >= zoomBoundaries.minY &&
                    item[selectedYAxisProperty] <= zoomBoundaries.maxY
                )
            }

            return true
        }) as ScatterSerie[]

    const series: ScatterSeriesType[] = [
        {
            datasetKeys: { id: 'id', x: selectedXAxisProperty, y: selectedYAxisProperty },
            color: theme.palette.FE.main,
            type: 'scatter',
        },
    ]
    const dropdownItems = Object.fromEntries(
        Object.entries(getOptionsGroups(issueType)).map(([header, optKey]) => [
            header,
            optKey
                .map((key) => ({ id: key, label: AXIS_OPTIONS_LABELS[key].label }))
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true })),
        ]),
    )

    const columns: GridColDef[] = [
        {
            field: 'label',
            headerName: 'Aktie',
            flex: 1,
        },
        {
            field: 'subEndDate',
            headerName: 'Datum',
            flex: 1,
            valueFormatter: (value: string) => formatDateYYYYMMDD(value, true),
            type: 'date' as GridColType,
        },
        {
            field: 'offerSize',
            headerName: 'Storlek',
            flex: 1,
            valueFormatter: (value: number) => formatBigMoney(value, { locale }),
            renderCell: ({ formattedValue, value }: GridRenderCellParams) => (
                <span title={formatMoney(value, { locale })}>{formattedValue}</span>
            ),
        },
        {
            field: selectedXAxisProperty,
            headerName: AXIS_OPTIONS_LABELS[selectedXAxisProperty].label,
            flex: 1,
            valueFormatter: (value: number, row: ScatterSerie) => formatValue(row, selectedXAxisProperty, { locale }),
            renderCell: ({ formattedValue, row }: GridRenderCellParams) => (
                <span title={formatValue(row, selectedXAxisProperty, { locale, short: false })}>{formattedValue}</span>
            ),
        },
        {
            field: selectedYAxisProperty,
            headerName: AXIS_OPTIONS_LABELS[selectedYAxisProperty].label,
            flex: 1,
            valueFormatter: (value: number, row: ScatterSerie) => formatValue(row, selectedYAxisProperty, { locale }),
            renderCell: ({ formattedValue, row }: GridRenderCellParams) => (
                <span title={formatValue(row, selectedYAxisProperty, { locale, short: false })}>{formattedValue}</span>
            ),
        },
    ].filter((col, index, arr) => arr.findIndex((_col) => _col.field === col.field) === index) // Filter unique fields

    const tableRows = selectionBoundaries
        ? seriesData.filter(
              (issue) =>
                  (issue[selectedXAxisProperty] as number) >= selectionBoundaries.minX &&
                  (issue[selectedXAxisProperty] as number) <= selectionBoundaries.maxX &&
                  (issue[selectedYAxisProperty] as number) >= selectionBoundaries.minY &&
                  (issue[selectedYAxisProperty] as number) <= selectionBoundaries.maxY,
          )
        : seriesData

    const handleItemClick = (item: ScatterSerie) => {
        const ids = gridRef.current?.getSortedRowIds()
        if (!ids) return

        const itemIndex = ids.findIndex((id) => item.id === id)

        gridRef.current?.setRowSelectionModel([item.id])
        gridRef.current?.setPage(Math.max(0, Math.floor(itemIndex / pageSize)))
    }

    return (
        <Grid container spacing={10}>
            <Grid size={{ xs: 12, md: 6 }}>
                <Grid container spacing={4}>
                    <Grid size={12}>
                        <Box>
                            <Stack direction='row' spacing={4}>
                                <DropdownSelect
                                    items={dropdownItems}
                                    label='X-axeln'
                                    onChange={([val]) => setSelectedXAxisProperty(val as AxisPropOption)}
                                    selectedItems={[selectedXAxisProperty]}
                                />
                                <DropdownSelect
                                    items={dropdownItems}
                                    label='Y-axeln'
                                    onChange={([val]) => setSelectedYAxisProperty(val as AxisPropOption)}
                                    selectedItems={[selectedYAxisProperty]}
                                />
                            </Stack>
                        </Box>
                    </Grid>
                    <Grid sx={{ width: '100%' }}>
                        <FLScatterChart
                            onItemClick={handleItemClick}
                            onSelect={handleSelectionChange}
                            onZoom={handleZoom}
                            selectedXAxisProperty={selectedXAxisProperty}
                            selectedYAxisProperty={selectedYAxisProperty}
                            series={series}
                            seriesData={seriesData}
                            xLabel={AXIS_OPTIONS_LABELS[selectedXAxisProperty].label}
                            yLabel={AXIS_OPTIONS_LABELS[selectedYAxisProperty].label}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant='h6' gutterBottom>
                    Emissioner
                </Typography>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <DataGridPro
                        {...exportDataGridProps}
                        apiRef={gridRef}
                        columns={columns}
                        loading={isLoading}
                        localeText={getMuiDataGridLocaleText(locale)}
                        onCellClick={(params) => {
                            window.open(`/issues/${params.id}`, '_blank')
                        }}
                        onPaginationModelChange={({ page, pageSize }) => {
                            setCurrentPage(page)
                            setPageSize(pageSize)
                        }}
                        pageSizeOptions={[10, 50]}
                        pagination
                        paginationModel={{ pageSize, page: currentPage }}
                        rows={tableRows}
                        sx={{
                            '& .MuiDataGrid-row': {
                                cursor: 'pointer',
                            },
                        }}
                    />
                </div>
            </Grid>
        </Grid>
    )
}
